// ============================================
// Patient-Facing File Upload Error Messages
// HLX-CAP.01 D#7 — Every rejection path gets
// a clear, actionable message. No cryptic codes.
// ============================================

/**
 * Every rejection type in the file ingestion pipeline.
 * Maps 1:1 to the use cases in hlx-cap-01-file-ingestion.md.
 */
export type FileRejectionCode =
  | 'FILE_TOO_LARGE'
  | 'WRONG_FORMAT'
  | 'CORRUPTED_FILE'
  | 'VIRUS_DETECTED'
  | 'PROCESSING_TIMEOUT'
  | 'TOO_MANY_PAGES'
  | 'SPOOFED_EXTENSION'
  | 'SERVICE_UNAVAILABLE'
  | 'RATE_LIMITED'
  | 'SESSION_EXPIRED'
  | 'ZIP_UPLOAD';

export interface FileUploadError {
  code: FileRejectionCode;
  /** Patient-facing title — short, plain language */
  title: string;
  /** Patient-facing message — what happened + what to do next */
  message: string;
}

/**
 * Format file size for display (e.g. "25 MB", "3.2 MB")
 */
function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
}

// ─── Error Builders ───
// Each function returns a patient-facing error with actionable guidance.
// Messages are written for someone who is NOT technical — no HTTP codes,
// no MIME types, no jargon.

/** UC-2: File exceeds 10MB size limit */
export function oversizedFileError(actualBytes: number): FileUploadError {
  return {
    code: 'FILE_TOO_LARGE',
    title: 'File too large',
    message: `Your file is ${formatSize(actualBytes)}. Lab reports are typically under 5 MB. Try uploading a lower-resolution scan or the original PDF from your lab portal.`,
  };
}

/** UC (type check): File is not PDF, JPG, or PNG */
export function wrongFormatError(fileName: string): FileUploadError {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return {
    code: 'WRONG_FORMAT',
    title: 'Unsupported file type',
    message: ext
      ? `We can't read .${ext} files. Please upload your lab results as a PDF, JPG, or PNG.`
      : 'This file type isn\u2019t supported. Please upload your lab results as a PDF, JPG, or PNG.',
  };
}

/** UC-3: File is structurally damaged / corrupted */
export function corruptedFileError(): FileUploadError {
  return {
    code: 'CORRUPTED_FILE',
    title: 'File appears damaged',
    message:
      'This file appears to be damaged. Try downloading it again from your lab\u2019s patient portal and re-uploading.',
  };
}

/** UC (virus scan): ClamAV detected a threat */
export function virusDetectedError(): FileUploadError {
  return {
    code: 'VIRUS_DETECTED',
    title: 'File could not be accepted',
    message:
      'This file did not pass our security check. Please download a fresh copy of your lab results from your lab\u2019s patient portal and try again.',
  };
}

/** UC-5: Pipeline exceeded 30s timeout */
export function processingTimeoutError(): FileUploadError {
  return {
    code: 'PROCESSING_TIMEOUT',
    title: 'Upload took too long',
    message:
      'Your upload is taking longer than expected. Please try again \u2014 most uploads complete in under 10 seconds.',
  };
}

/** UC-8: PDF exceeds 10-page limit */
export function tooManyPagesError(pageCount: number): FileUploadError {
  return {
    code: 'TOO_MANY_PAGES',
    title: 'Too many pages',
    message: `Lab reports are usually under 10 pages. This file has ${pageCount} pages. Upload just the pages with your test results, or photograph the results pages with your camera.`,
  };
}

/**
 * UC-6: Spoofed file extension detected (magic bytes don't match extension).
 * SECURITY: Returns a generic message — no details about what was detected.
 * This prevents attackers from learning about our validation pipeline.
 * Exact copy from UC-6 per c3 acceptance criteria.
 */
export function spoofedExtensionError(): FileUploadError {
  return {
    code: 'SPOOFED_EXTENSION',
    title: 'File could not be accepted',
    message:
      'We couldn\u2019t process that file. Please upload a PDF or image of your lab report.',
  };
}

/** ClamAV or pipeline service is down */
export function serviceUnavailableError(): FileUploadError {
  return {
    code: 'SERVICE_UNAVAILABLE',
    title: 'Temporarily unavailable',
    message:
      'Our upload system is briefly unavailable. Please try again in a few minutes.',
  };
}

/** Rate limit exceeded (IP or session) */
export function rateLimitedError(): FileUploadError {
  return {
    code: 'RATE_LIMITED',
    title: 'Too many uploads',
    message:
      'You\u2019ve uploaded several files in a short time. Please wait a few minutes before trying again.',
  };
}

/** Session expired before or during upload */
export function sessionExpiredError(): FileUploadError {
  return {
    code: 'SESSION_EXPIRED',
    title: 'Session expired',
    message:
      'Your session has timed out. Please refresh the page and upload your lab results again.',
  };
}

/** Patient attempted to upload a ZIP archive */
export function zipUploadError(): FileUploadError {
  return {
    code: 'ZIP_UPLOAD',
    title: 'ZIP files aren\u2019t supported',
    message:
      'It looks like you uploaded a ZIP file. Please unzip it first, then upload the lab report PDF or image inside.',
  };
}

// ─── Lookup by code ───

const ERROR_BUILDERS: Record<FileRejectionCode, () => FileUploadError> = {
  FILE_TOO_LARGE: () => oversizedFileError(0),
  WRONG_FORMAT: () => wrongFormatError(''),
  CORRUPTED_FILE: corruptedFileError,
  VIRUS_DETECTED: virusDetectedError,
  PROCESSING_TIMEOUT: processingTimeoutError,
  TOO_MANY_PAGES: () => tooManyPagesError(0),
  SPOOFED_EXTENSION: spoofedExtensionError,
  SERVICE_UNAVAILABLE: serviceUnavailableError,
  RATE_LIMITED: rateLimitedError,
  SESSION_EXPIRED: sessionExpiredError,
  ZIP_UPLOAD: zipUploadError,
};

/**
 * Get a patient-facing error by rejection code.
 * Use the specific builder functions when you have context (file size, page count).
 * Use this lookup only when you have just a code.
 */
export function getUploadErrorByCode(code: FileRejectionCode): FileUploadError {
  return ERROR_BUILDERS[code]();
}
